import PropTypes from 'prop-types';

import { useAuth } from '#globalContexts/AuthProvider';

// Toggle via env var (REACT_APP_MAINTENANCE_MODE=true) + redeploy.
// When active, only users on the internal 'owner' plan can access the app;
// everyone else sees the maintenance screen. This is a frontend-only login gate
// — it does not block direct API/token access to the backend.
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

// Public auth routes must stay reachable during maintenance so the owner can log
// in (the plan check only runs once a session exists). OAuth callbacks are kept
// open too, otherwise an in-progress login flow would break mid-redirect.
// Note: this gate renders outside the Router, so we read the raw pathname here.
const PUBLIC_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/oauth/callback'];

const isPublicPath = () =>
	typeof window !== 'undefined' && PUBLIC_PATHS.some((path) => window.location.pathname.startsWith(path));

function MaintenanceScreen() {
	return (
		<div className="fixed inset-0 flex items-center justify-center bg-gray-50 p-6">
			<div className="max-w-md text-center">
				<h1 className="text-lg font-semibold text-text">Wartungsarbeiten</h1>
				<p className="mt-3 text-text">
					Wir führen gerade kurze Wartungsarbeiten durch. Die Anwendung ist in Kürze wieder verfügbar. Bitte
					versuche es später erneut.
				</p>
			</div>
		</div>
	);
}

/**
 * Gates the entire app behind a maintenance screen when REACT_APP_MAINTENANCE_MODE
 * is active. Users on the internal 'owner' plan pass through; everyone else sees
 * the maintenance screen — except on public auth routes, which stay open so the
 * owner can log in.
 *
 * Relies on AuthProvider blocking render until orgInfo is loaded, so orgPlan is
 * already resolved by the time this renders.
 */
function MaintenanceGate({ children }) {
	const { orgPlan } = useAuth();

	if (MAINTENANCE_MODE && orgPlan !== 'owner' && !isPublicPath()) {
		return <MaintenanceScreen />;
	}

	return children;
}

MaintenanceGate.propTypes = {
	children: PropTypes.node.isRequired,
};

export default MaintenanceGate;
